import * as React from "react";
import { useToasts } from "react-toast-notifications";
import { IClient } from "intake_shared/src/types";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import { Dropdown } from "../../../bits/Dropdown/Dropdown";
import FilterButton from "../../../bits/FilterButton/FilterButton";
import { cn } from "../../../../helpers";
import { useLeaWorkflow } from "./LeaWorkflowContext";
import { leaStatusSummary } from "./leaWorkflow";
import { canHandToLea } from "./leaHandoff";
import LeaHandoffDialog from "./LeaHandoffDialog";
import styles from "./LeaStatus.module.scss";

interface LeaStatusProps {
    client: IClient;
    locked?: boolean;
}

export default function LeaStatus({ client, locked = false }: LeaStatusProps) {
    const { addToast } = useToasts();
    const lea = useLeaWorkflow();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [handoffOpen, setHandoffOpen] = React.useState(false);

    if (!lea) return null;

    const status = lea.workflow ? leaStatusSummary(lea.workflow) : null;
    const canHandOff =
        !status && lea.notStarted && !lea.error && !locked && canHandToLea(client);
    if (!status && !canHandOff) return null;

    const handleTakeover = async () => {
        setConfirmOpen(false);
        const succeeded = await lea.runAction("takeover");
        addToast(
            succeeded
                ? "Intake has taken over from Lea AI."
                : "Could not take over from Lea AI. Please try again.",
            { appearance: succeeded ? "success" : "error" }
        );
    };

    const menu =
        status?.canTakeOver && !locked
            ? { label: "Take over from Lea AI", onClick: () => setConfirmOpen(true) }
            : canHandOff
            ? { label: "Hand to Lea AI", onClick: () => setHandoffOpen(true) }
            : null;
    const label = status?.label ?? "Not started";
    const tone = status?.tone ?? "neutral";

    return (
        <div className={styles.metaBox}>
            <span className={styles.metaLabel}>LEA AI:</span>
            <span className={styles.metaValue}>
                {menu ? (
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton
                                className={cn(styles.filterButton, styles[tone])}
                                text={label}
                                disabled={lea.pending}
                            />
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <Dropdown.Item onClick={menu.onClick}>
                                {menu.label}
                            </Dropdown.Item>
                        </Dropdown.Content>
                    </Dropdown>
                ) : (
                    <span className={styles[tone]}>{label}</span>
                )}
            </span>
            <AlertDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Take over from Lea AI?"
                description="Lea AI will stop texting this client and Intake will handle the conversation."
                saveText="Take over"
                onSaveClick={handleTakeover}
            />
            {handoffOpen && (
                <LeaHandoffDialog
                    client={client}
                    onClose={() => setHandoffOpen(false)}
                    onHandedOff={() => lea.refresh()}
                />
            )}
        </div>
    );
}
